import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS, SORT_OPTIONS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { getQueryParams } from "@/utils/httpRequest";
import { showNotification } from "../actions/notification";
import { setProcessingState } from "../reducers/modalConfirmation";
import { baseUrl, onPrepareHeaders } from "./helpers";

export const defaultLoanNoteFilters = {
  page: 1,
  size: 5,
  sortBy: SORT_OPTIONS[1].id,
  searchTerm: "",
};

export const loanNoteService = createApi({
  reducerPath: "loanNoteApi",
  tagTypes: ["LoanNote"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getLoanNotes: builder.query({
      query: ({ loanId, ...params }) => `loans/${loanId}/notes?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [...result.items.map(({ loanId }) => ({ type: "LoanNote", loanId })), { type: "LoanNote", id: "LIST" }]
          : [{ type: "LoanNote", id: "LIST" }],
    }),
    addLoanNote: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `loans/${loanId}/notes`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "LoanNote", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_NOTE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been added to the loan!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateLoanNote: builder.mutation({
      query: ({ loanId, loanNoteId, ...body }) => ({
        url: `loans/${loanId}/notes/${loanNoteId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "LoanNote", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_NOTE_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteLoanNote: builder.mutation({
      query: ({ loanId, loanNoteId }) => ({
        url: `loans/${loanId}/notes/${loanNoteId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "LoanNote", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LOAN_NOTE_DELETED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Note has been deleted!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
        } catch (err) {
          dispatch(setProcessingState(false));
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const { useGetLoanNotesQuery, useAddLoanNoteMutation, useUpdateLoanNoteMutation, useDeleteLoanNoteMutation } =
  loanNoteService;
